import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kTemplateSelectionApiPath } from '@/constants/constants-api-paths'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { ApiError } from '@/util/errors/api-error'

/**
 * Clear a draft's template selection. Idempotent — a 404 means there was nothing to clear, which
 * is treated as success.
 */
export async function deleteTemplateSelection(artifactId: string): Promise<void> {
  const sentry_transaction_id = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error(kErrorCodeAuthError)

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kTemplateSelectionApiPath(artifactId)}`
    const response = await fetch(apiUrl, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Transaction-Id': sentry_transaction_id,
      },
    })

    if (!response.ok && response.status !== 404) {
      const data = (await response.json().catch(() => ({}))) as { detail?: unknown }
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error(`Clear template selection failed - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`), {
          extra: { requestUrl: apiUrl, artifactId, data: JSON.stringify(data) },
        })
      })
      throw new ApiError(response.status, `Failed to clear template selection: ${response.status}`)
    }
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(error)
      })
    }
    throw error
  }
}
